'use client';

/**
 * Shared hook for the obeya cards — resolves projectId from the
 * viewport selection chain and runs the same `obeya.detail` strategy
 * that today's monolithic `ObeyaWidget` uses. React Query dedupes
 * across cards so 5 cards on the canvas = 1 BFF call.
 *
 * @see klyde_ipe/components/canvas/cards/obeya/
 */

import { useObeyaProject } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useViewportSelection } from '@/lib/stores/viewport-selection';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';

export function useObeyaProjectData() {
  // projectId resolution: prefer the viewport selection's `projectId`
  // (parent project of whatever's selected), fall back to the graph
  // store's projectId, and treat a project-level selection as
  // self-referential. Mirrors the resolution `ObeyaWidget` does today.
  const { entityType, entityId, projectId: vsProjectId } = useViewportSelection();
  const storeProjectId = useGraphSelectionStore((s) => s.projectId);
  const projectId =
    entityType === 'project' ? entityId : (vsProjectId ?? storeProjectId);

  const detailStrategy = useCapabilityStore((s) => s.strategyName('obeya', 'detail'));

  const result = useObeyaProject(
    detailStrategy ?? 'obeya_detail',
    projectId ? { project_id: projectId, include_children: true } : null,
  );

  // Surface the queryKey so cards can pass it straight to useCardExport
  // without reconstructing it (capture doctrine rule 1). Matches
  // useObeyaProject's key shape.
  const queryKey = ['obeya-project', detailStrategy ?? 'obeya_detail', projectId] as const;

  return { ...result, projectId, queryKey };
}
